/**
 * 「复制单位地址」到教材邮寄地址（2026-11-01 用户要求）
 * -----------------------------------------------------------------------------
 * 背景：报名表单里「单位所在地」（`unitRegion`，region 字段）与下面那张
 * **教材邮寄地址** 卡（走 `POST /address`，不是表单字段）经常是同一个地方 ——
 * 在职学员让教材寄到单位，就得把省 / 市 / 区 / 详细地址再选一遍、再打一遍。
 *
 * 所以卡片右上角给一个「复制单位地址」，一键把表单里的单位地址抄过去。
 *
 * 三条边界（都在这里判定，页面只负责画）：
 *   1. **当前表单没有单位地址就不显示按钮** —— 不是所有报名类型都有这个题；
 *      「有没有」以字段定义为准，不是写死一个 key：后台把 key 改名（`unitRegion`
 *      → `companyRegion`）后按钮不该消失，所以 key 命中后还有一层「名称含"单位"」兜底。
 *   2. **条件隐藏的单位地址不参与** —— 隐藏的字段在界面上根本看不见，
 *      复制一个看不见的答案会让学员莫名其妙（`isFieldVisible` 与校验器同一份判定）。
 *   3. **只复制地址本身** —— 省 / 市 / 区码 + 详细地址，**不碰**收件人 / 手机号：
 *      那两个在报名阶段根本不由学员填（领取教材时按报名人姓名 + 注册手机号带入）。
 */
import { FieldType, type FormFieldDef, isFieldVisible, normalizeRegionValue } from '@enroll/shared';

/** 约定的字段 key（seed 里一直是它）；命中优先，但不作为唯一判据 */
const UNIT_REGION_KEY = 'unitRegion';
/** 名称兜底：后台改过 key 但语义没变时，还能认出「单位所在地」 */
const UNIT_LABEL_HINT = '单位';

/**
 * 在当前 Schema 里找「单位地址」字段；找不到返回 `null`（按钮据此不渲染）。
 *
 * 为什么不是「第一个 region 字段」：同一张表单里可能有多个 region（单位地址、
 * 户籍地址…），随手拿第一个会在某些类型上把「户籍地址」当成单位地址复制过去 ——
 * 错的地址比没有按钮更糟。
 */
export function findWorkUnitRegionField(
  fields: readonly FormFieldDef[],
  answers: Record<string, unknown>,
): FormFieldDef | null {
  const visibleRegions = fields.filter(
    (field) => field.type === FieldType.REGION && isFieldVisible(field, answers),
  );
  if (visibleRegions.length === 0) return null;

  const byKey = visibleRegions.find((field) => field.key === UNIT_REGION_KEY);
  if (byKey) return byKey;

  return visibleRegions.find((field) => field.label.includes(UNIT_LABEL_HINT)) ?? null;
}

/** 可复制的地址片段（与 `ShippingAddressInput` 的地址部分同形状） */
export interface RegionAddressPatch {
  provinceCode: string;
  cityCode: string;
  districtCode: string;
  detail: string;
}

/**
 * 单位地址值 → 邮寄地址补丁。
 *
 * 一律过 `normalizeRegionValue`：它会把「父级不成立」的脏子码丢掉，
 * 免得把控件回传的错配组合（省改了、市还是旧的）抄进邮寄地址。
 */
export function regionToAddressPatch(value: unknown): RegionAddressPatch {
  const region = normalizeRegionValue(value);
  return {
    provinceCode: region.provinceCode,
    cityCode: region.cityCode,
    districtCode: region.districtCode,
    detail: region.detail,
  };
}

/**
 * 单位地址是否「有东西可复制」。
 *
 * 判据放得很松（只要有省码或详细地址），不放 `isRegionComplete`：
 * 单位地址是**选填**，学员可能只填了「广东省 / 深圳市」还没来得及选区，
 * 这时复制过去至少让他少选两级 —— 补齐与否由邮寄地址自己的校验去管。
 */
export function hasCopyableRegion(value: unknown): boolean {
  const region = normalizeRegionValue(value);
  return Boolean(region.provinceCode) || region.detail.trim() !== '';
}

/**
 * 邮寄地址是否已经有内容（决定要不要弹「会覆盖」确认）。
 *
 * 只看到「省码 / 详细地址」两项：收件人与手机号在报名阶段恒为空，
 * 拿它们判断会让确认框在任何情况下都弹出来。
 */
export function addressHasContent(address: { provinceCode?: string; detail?: string }): boolean {
  return Boolean(address.provinceCode) || (address.detail ?? '').trim() !== '';
}
